import { BiAsset, BiBadge, BiButton, BiButtonGroup, BiModal, BiTypography } from 'bi-web-components-react';
import { BiIconCross, BiIconCart } from '@bi-digital/icons-react';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteCartItem, signIn } from '../../api';
import { useCartContext } from '../../context/CartContext';
import useRedirectLoading from '../../hooks/useRedirectLoading';
import { BiaxCartItem } from '../../types';
import { openCart } from '../../utils';
import CartItem from './cartItem/cartItem';
import classNames from 'classnames';

import styles from './cart.module.scss';
import { trackGAEventForApplication } from '@bino/utils/functions';

export default function Cart() {
    const { t } = useTranslation(['cart', 'common']);
    const {
        iconColor,
        isAuthorized,
        isFallbackPage,
        cart,
        refreshCart,
        loadingCart,
        error,
        productSearchUrl,
        disableCart,
    } = useCartContext();

    const { redirectLoading, startRedirectLoading } = useRedirectLoading();
    const [deleteError, setDeleteError] = useState<boolean>(false);
    const [itemBeingDeleted, setItemBeingDeleted] = useState<string | null>(null);
    const [count, setCount] = useState(cart.items.length === 0 ? undefined : cart.items.length);

    const goToApplication = () => {
        startRedirectLoading();
        trackGAEventForApplication(
            'goToApplication',
            cart.items.map((item) => ({
                id: String(item.productId),
                name: item.name,
                campus: t('cart:campus.' + item.campusCode),
            })),
        );
        window.location.href = cart?.biaxCartDetailPageUrl ?? '';
    };

    const removeFromShoppingCart = async (item: BiaxCartItem) => {
        try {
            setItemBeingDeleted(item.bannerId);
            setDeleteError(false);
            await deleteCartItem(item.bannerId);

            const updatedItems = cart.items.filter(({ bannerId }) => bannerId !== item.bannerId);
            await refreshCart((c) => ({ ...c, items: updatedItems }));
            setItemBeingDeleted(null);
        } catch {
            setDeleteError(true);
            setItemBeingDeleted(null);
        }
    };

    const login = async () => {
        startRedirectLoading();
        await signIn();
    };

    const toggleCartModal = async () => {
        removeVoiceText();
        openCart();
    };

    function removeVoiceText() {
        const voiceText = document.getElementById('voiceText');
        if (voiceText) {
            voiceText.parentNode?.removeChild(voiceText);
        }
    }

    function getMoreStudiesLink(fallbackPage: boolean) {
        let domain = window.location.hostname;

        if (fallbackPage) {
            if (domain.includes('bi.no')) {
                domain = domain.replace('bi.no', 'bi.edu');
                return 'https://' + domain + productSearchUrl;
            }
        }
        return productSearchUrl;
    }

    const isAuthorizedAndCartItemsLoaded = isAuthorized && !loadingCart;

    useEffect(() => {
        setCount(cart.items.length === 0 ? undefined : cart.items.length);
    }, [cart]);

    if (disableCart) {
        return null;
    }

    return (
        <>
            <BiButton
                icon
                variant={count ? 'flat' : 'text'}
                size='medium'
                onClick={toggleCartModal}
                dark={iconColor !== 'primary'}
            >
                <BiIconCart title={t('cart:AriaLabelShoppingCart')} />
                <span className={classNames(styles.cartLabel, !count && styles.hideOnMobile)}>
                    {count ? count : t('cart:shoppingcart')}
                </span>
            </BiButton>

            <BiModal
                id='cart-content'
                modalId='cart-content'
                top='80'
                slideIn
                slideInButtonMobile
                alignment='right'
                blurOverlay
            >
                <div className={styles.cartContent}>
                    <div>
                        <div className={styles.cartHeaderContainer}>
                            <div className={styles.cartHeader}>
                                {count ? (
                                    <BiBadge
                                        position='top-right'
                                        count={count}
                                        aria-label={`${cart.items.length} ${t('cart:AriaLabelBadgeCount')}`}
                                    >
                                        <BiIconCart size='large' color='gray' aria-hidden='true' />
                                    </BiBadge>
                                ) : (
                                    <BiIconCart size='large' color='gray' aria-hidden='true' />
                                )}
                                <BiTypography fontSize='large' fontStyle='bold'>
                                    {t('cart:shoppingcart')}
                                </BiTypography>
                            </div>
                            <BiButton
                                icon
                                variant='text'
                                onClick={() => openCart()}
                                aria-label={t('cart:AriaLabelCloseCart')}
                            >
                                <BiIconCross aria-hidden='true' />
                            </BiButton>
                        </div>

                        {!isAuthorized && (
                            <CartStatus message={t('cart:notAuthorized')}>
                                <BiButton
                                    stretch
                                    name='signIn'
                                    submitting={redirectLoading}
                                    onClick={login}
                                    disabled={redirectLoading}
                                >
                                    {t('common:login')}
                                </BiButton>
                            </CartStatus>
                        )}

                        {isAuthorizedAndCartItemsLoaded && Boolean(error) && (
                            <CartStatus message={t('cart:errorGettingCart')} />
                        )}
                        {isAuthorizedAndCartItemsLoaded && !error && cart.items.length === 0 && (
                            <CartStatus message={t('cart:emptyCart')} />
                        )}

                        {cart.items.length > 0 && (
                            <>
                                <BiTypography>
                                    <p role='presentation'>{t('cart:priority')}</p>
                                </BiTypography>

                                <div className={styles.cartItemsContainer}>
                                    {cart.items.map((item, idx) => (
                                        <CartItem
                                            item={item}
                                            itemBeingDeleted={itemBeingDeleted}
                                            errorDeletingCartItem={deleteError}
                                            removeFromCart={() => removeFromShoppingCart(item)}
                                            key={`${item.bannerId} + ${idx}`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {isAuthorizedAndCartItemsLoaded && (
                        <BiButtonGroup top='large'>
                            {cart.items.length > 0 && (
                                <BiButton
                                    stretch
                                    name='gotoapplication'
                                    onClick={goToApplication}
                                    disabled={redirectLoading}
                                    submitting={redirectLoading}
                                >
                                    {t('cart:gotoapplication')}
                                </BiButton>
                            )}
                            <BiButton
                                stretch
                                variant='outlined'
                                name='continuetostudies'
                                href={getMoreStudiesLink(isFallbackPage)}
                            >
                                {t('cart:continuetostudies')}
                            </BiButton>
                        </BiButtonGroup>
                    )}
                </div>
            </BiModal>
        </>
    );
}

function CartStatus({ message, children }: { message: string; children?: ReactNode }) {
    return (
        <div className={styles.emptyCart}>
            <BiAsset asset='libraryLarge'></BiAsset>
            <BiTypography>
                <p role='presentation'>{message}</p>
            </BiTypography>
            {children}
        </div>
    );
}
